/**
 * ResetPassword.tsx — Public password reset page (Phase 9 SC-4, D-09-14, D-09-19).
 *
 * Two states:
 *   State A — No token in URL (/reset-password):
 *     Email input → POST /users/reset-password/request → always shows success
 *     card (non-enumerating: T-09-20 — never reveal whether email exists)
 *
 *   State B — Token present (/reset-password?token=XXX):
 *     New + confirm password → POST /users/reset-password/complete
 *     → success: navigate /login?reset=1
 *     → invalid token: error card
 *
 * D-09-19 (role-correct deep link): the API builds the reset link using
 * CHATTER_APP_BASE_URL for chatters, so this page at :5174 is the correct target.
 *
 * Layout mirrors Login.tsx — centered card, ThemeSwitch top-right, "Chatter workspace" subtitle.
 * Conforms to 09-UI-SPEC Screen 4.
 */

import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { operatorsApi, ApiRequestError } from '../lib/api-client.js';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  ThemeSwitch,
} from '@crm/ui';

export function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const hasToken = token.length > 0;

  // Update page title per 09-UI-SPEC Accessibility Contract
  React.useEffect(() => {
    document.title = 'MaloumDash — Reset Password';
  }, []);

  // ---------------------------------------------------------------------------
  // State A — Request reset
  // ---------------------------------------------------------------------------

  const [email, setEmail] = React.useState('');
  const [requestSent, setRequestSent] = React.useState(false);
  const [requestError, setRequestError] = React.useState<string | null>(null);

  const requestMut = useMutation({
    mutationFn: () => operatorsApi.requestReset({ email: email.trim() }),
    onSuccess: () => {
      // Always show success (non-enumerating — T-09-20)
      setRequestSent(true);
    },
    onError: () => {
      // Also show success on error (non-enumerating — never reveal whether email exists)
      setRequestSent(true);
    },
  });

  // ---------------------------------------------------------------------------
  // State B — Complete reset
  // ---------------------------------------------------------------------------

  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [completeError, setCompleteError] = React.useState<string | null>(null);
  const [tokenInvalid, setTokenInvalid] = React.useState(false);

  const completeMut = useMutation({
    mutationFn: () => operatorsApi.completeReset({ token, newPassword }),
    onSuccess: () => {
      navigate('/login?reset=1', { replace: true });
    },
    onError: (err) => {
      if (err instanceof ApiRequestError) {
        const msg =
          typeof err.body.message === 'string'
            ? err.body.message
            : err.body.message.join('; ');
        if (
          err.status === 400 ||
          err.status === 404 ||
          msg.toLowerCase().includes('invalid') ||
          msg.toLowerCase().includes('expired')
        ) {
          setTokenInvalid(true);
        } else {
          setCompleteError(msg);
        }
      } else {
        setCompleteError((err as Error).message ?? 'Something went wrong');
      }
    },
  });

  const handleCompleteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCompleteError(null);
    if (newPassword !== confirmPassword) {
      setCompleteError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setCompleteError('Password must be at least 8 characters.');
      return;
    }
    completeMut.mutate();
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4">
        <ThemeSwitch />
      </div>
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-xl font-bold text-text">MaloumDash</h1>
          {/* D-09-19: subtitle identifies the chatter-app target (vs Admin console in admin-app) */}
          <p className="text-sm text-text-muted mt-1">Chatter workspace</p>
        </div>

        {/* State A — Request reset */}
        {!hasToken && (
          <>
            {requestSent ? (
              /* Always-success message card (non-enumerating, T-09-20) */
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-text" role="status" aria-live="polite">
                    If an account exists for that email address, a password reset
                    link has been sent. Check your inbox.
                  </p>
                </CardContent>
                <CardFooter>
                  <a href="/login">
                    <Button variant="outline">Back to sign in</Button>
                  </a>
                </CardFooter>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Reset Password</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={(e) => { e.preventDefault(); requestMut.mutate(); }} className="flex flex-col gap-4">
                    <p className="text-sm text-text-muted">
                      Enter your email address. If an account exists, you will receive
                      a reset link.
                    </p>
                    <Input
                      label="Email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setRequestError(null);
                      }}
                      disabled={requestMut.isPending}
                    />
                    {requestError && (
                      <p className="text-sm text-danger" role="alert">
                        {requestError}
                      </p>
                    )}
                    <Button
                      type="submit"
                      loading={requestMut.isPending}
                      className="w-full justify-center"
                    >
                      Send Reset Link
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
            <p className="text-center text-sm text-text-muted mt-4">
              <a href="/login" className="text-accent">
                Back to sign in
              </a>
            </p>
          </>
        )}

        {/* State B — Complete reset (token present) */}
        {hasToken && (
          <>
            {tokenInvalid ? (
              <Card>
                <CardContent className="flex flex-col gap-4 pt-4">
                  <p className="text-sm text-danger" role="alert">
                    This reset link is invalid or has expired.
                  </p>
                  <a href="/login" className="text-accent text-sm">
                    Return to sign in
                  </a>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Set New Password</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCompleteSubmit} className="flex flex-col gap-4">
                    <Input
                      label="New password"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setCompleteError(null);
                      }}
                      disabled={completeMut.isPending}
                    />
                    <Input
                      label="Confirm new password"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setCompleteError(null);
                      }}
                      disabled={completeMut.isPending}
                    />
                    {completeError && (
                      <p className="text-sm text-danger" role="alert">
                        {completeError}
                      </p>
                    )}
                    <Button
                      type="submit"
                      loading={completeMut.isPending}
                      className="w-full justify-center"
                    >
                      Reset Password
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
